import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { CATEGORIES, TYPE_LABELS } from "../lib/categories";
import CityAutocomplete from "../components/CityAutocomplete";
import { Upload, X, Image as ImageIcon, AlertCircle, CheckCircle2, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function CreateListing() {
  const { lang, t } = useI18n();
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("tools");
  const [city, setCity] = useState("");
  const [price, setPrice] = useState("");
  const [listingType, setListingType] = useState("sell");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingListing, setLoadingListing] = useState(isEdit);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!isEdit || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/listings/${editId}`);
        const l = r.data;
        if (cancelled) return;
        if (l.user_id !== user.user_id) {
          toast.error("Tai ne tavo skelbimas");
          navigate("/profile");
          return;
        }
        setTitle(l.title);
        setDescription(l.description);
        setCategory(l.category);
        setCity(l.city);
        setPrice(String(l.price || ""));
        setListingType(l.listing_type);
        setImages((l.images || []).map((p) => ({ path: p, preview: fileUrl(p) })));
      } catch (_e) {
        toast.error("Skelbimas nerastas");
        navigate("/profile");
      }
      setLoadingListing(false);
    })();
    return () => { cancelled = true; };
  }, [isEdit, editId, user, navigate]);

  if (!user) {
    if (authLoading) {
      return <div className="py-20 text-center text-[#7A7A73]">...</div>;
    }
    return (
      <div className="max-w-md mx-auto py-20 px-6 text-center" data-testid="login-required-page">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C85A32]"/>
        <p className="mt-4 font-heading font-bold text-2xl text-[#1A1A18]">{t("login_required")}</p>
        <button onClick={login} data-testid="login-prompt-button" className="mt-6 btn-primary">
          {t("cta_login")}
        </button>
      </div>
    );
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 6) {
      toast.error("Max 6 photos");
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const compressed = await compressImage(file, 1600, 0.85);
        const fd = new FormData();
        fd.append("file", compressed);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { path: r.data.path, preview: URL.createObjectURL(compressed) }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload failed");
      }
    }
    setUploading(false);
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const runAi = async () => {
    if (!images.length) {
      toast.error(t("ai_autofill_hint"));
      return;
    }
    const target = images.find((i) => i.path);
    if (!target) return;
    setAiBusy(true);
    try {
      const r = await api.post("/ai/analyze-listing", { path: target.path }, { timeout: 90000 });
      const d = r.data || {};
      // Use the title/description matching the user's current UI language.
      const pickTitle = d[`title_${lang}`] || d.title_lt || d.title_en || "";
      const pickDesc = d[`description_${lang}`] || d.description_lt || d.description_en || "";
      if (pickTitle) setTitle(pickTitle);
      if (pickDesc) setDescription(pickDesc);
      // Only auto-set the category if the AI was reasonably confident — we'd
      // rather leave it blank than slap "other" on every listing.
      if (d.category && d.category !== "other" && (d.confidence ?? 0) >= 0.4) {
        setCategory(d.category);
      }
      if (typeof d.suggested_price_eur === "number" && d.suggested_price_eur > 0 && listingType !== "free") {
        setPrice(String(d.suggested_price_eur));
      }
      setAiUsed(true);
      // If confidence was low, hint to the user that they should double-check.
      const conf = d.confidence ?? 0;
      if (conf < 0.4 || !pickTitle) {
        toast.warning(t("ai_low_conf"));
      } else {
        toast.success(t("ai_filled"));
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`${t("ai_failed")} (${detail})`);
    }
    setAiBusy(false);
  };

  const submit = async () => {
    if (!title.trim() || !description.trim() || !city) {
      toast.error("Pildyk visus laukus");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title, description, category, city,
        price: listingType === "free" ? 0 : parseFloat(price || 0),
        listing_type: listingType,
        images: images.map((i) => i.path),
      };
      let listing;
      if (isEdit) {
        const r = await api.patch(`/listings/${editId}`, payload);
        listing = r.data;
        toast.success("Skelbimas atnaujintas");
      } else {
        const r = await api.post("/listings", payload);
        listing = r.data;
        toast.success(t("listing_active"));
      }
      navigate(`/listing/${listing.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl font-heading font-black tracking-tight text-[#1A1A18] inline-flex items-center gap-3">
        {isEdit ? <><Pencil className="w-7 h-7 text-[#C85A32]"/> Redaguoti skelbimą</> : t("new_listing")}
      </h1>

      {!isEdit && (
        <div className="mt-4 bg-[#2A5C38] text-white p-4 rounded-md flex items-start gap-3" data-testid="free-banner">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5"/>
          <div>
            <p className="font-bold">100% NEMOKAMA</p>
            <p className="text-sm text-white/85">Visi skelbimai – nemokami. Be paslėptų mokesčių.</p>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-5">
        {/* Photos */}
        <div>
          <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">
            {t("upload_images")}
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className="aspect-square relative bg-[#F0EFEB] border border-[#D1CFC7] rounded-md overflow-hidden" data-testid={`image-preview-${idx}`}>
                <img src={img.preview} alt="" className="w-full h-full object-cover"/>
                <button
                  data-testid={`image-remove-${idx}`}
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-[#C85A32]"
                >
                  <X className="w-3 h-3"/>
                </button>
              </div>
            ))}
            {images.length < 6 && (
              <label className="aspect-square border-2 border-dashed border-[#D1CFC7] rounded-md flex flex-col items-center justify-center cursor-pointer hover:bg-[#F0EFEB] transition-colors text-[#7A7A73]" data-testid="image-upload-zone">
                <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} data-testid="image-upload-input" />
                {uploading ? <div className="text-xs">...</div> : <><Upload className="w-5 h-5"/><span className="text-xs mt-1">+</span></>}
              </label>
            )}
          </div>
          {!isEdit && (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
              <button
                type="button"
                data-testid="ai-autofill-button"
                onClick={runAi}
                disabled={aiBusy || uploading || !images.some((i) => i.path)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm border border-[#C85A32] text-[#C85A32] bg-white hover:bg-[#FFF1EB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                {aiBusy ? t("ai_analyzing") : t("ai_autofill")}
              </button>
              <span className="text-xs text-[#7A7A73] leading-snug" data-testid="ai-autofill-hint">
                {aiUsed ? "✓ " : ""}{t("ai_autofill_hint")}
              </span>
            </div>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">{t("title")}</label>
          <input
            data-testid="listing-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bosch drelė GSB 13 RE"
            className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">{t("description")}</label>
          <textarea
            data-testid="listing-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Naudota 2 metus, gera būklė..."
            rows={5}
            className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
          />
        </div>

        {/* Category & City */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">{t("category")}</label>
            <select
              data-testid="listing-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
            >
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c[lang]}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">{t("city")}</label>
            <CityAutocomplete value={city} onChange={setCity} testId="listing-city-input" />
          </div>
        </div>

        {/* Type & Price */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">{t("type")}</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <button
                  type="button"
                  key={k}
                  data-testid={`type-${k}`}
                  onClick={() => setListingType(k)}
                  className={`px-3 py-2.5 text-sm font-semibold rounded-md border transition-colors ${
                    listingType === k
                      ? "bg-[#1A1A18] text-white border-[#1A1A18]"
                      : "bg-white text-[#4A4A45] border-[#D1CFC7] hover:border-[#1A1A18]"
                  }`}
                >
                  {v[lang]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73] mb-2">
              {listingType === "auction" ? t("starting_price") : t("price")} {listingType === "free" ? `(${t("free")})` : "€"}
            </label>
            <input
              data-testid="listing-price-input"
              disabled={listingType === "free"}
              type="number"
              min="0"
              step="0.01"
              value={listingType === "free" ? "0" : price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="25.00"
              className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32] disabled:bg-[#F0EFEB]"
            />
          </div>
        </div>

        <button
          data-testid="listing-submit-button"
          onClick={submit}
          disabled={submitting || loadingListing}
          className="w-full btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? "..." : isEdit ? "Išsaugoti pakeitimus" : t("publish")}
        </button>
      </div>
    </div>
  );
}
